import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';
import {graphql, useFragment, useMutation} from 'react-relay';
import {Form, Button} from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup';

import SpinnerWrapper from '../../atoms/SpinnerWrapper';
import ExtensionAddendumSubmitMethod from './ExtensionAddendumSubmitMethod';
import UploadExtensionAddendum from './UploadExtensionAddendum';
import {ADDITIONAL_APPLICANT_SLOTS, ADDENDUM_SUBMIT_METHOD} from '../../../constants';

const extensionAddendumSubmitOptionsFragment = graphql`
  fragment ExtensionAddendumSubmitOptions_AddendumPage_Fragment on AccessRequest {
    iid
    user {
      country
    }
    accessRequestDraft {
      id
      isPi
      additionalApplicants {
        name
        email
      }
    }
  }
`;

const submitExtensionAddendumDocusignMutation = graphql`
  mutation ExtensionAddendumSubmitOptions_Mutation(
    $eaInput: SubmitExtensionAddendumDocusignMutationInput!
  ) {
    submitExtensionAddendumDocusign(input: $eaInput) {
      accessRequestDraft {
        id
        contemplatedUse
      }
      error {
        type
        message
      }
    }
  }
`;

const ExtensionAddendumSubmitOptions = ({queryData, setUploadSuccess}) => {
  const navigate = useNavigate();

  const {iid, user, accessRequestDraft} = useFragment(
    extensionAddendumSubmitOptionsFragment,
    queryData
  );
  const [submitMethod, setSubmitMethod] = useState('');
  const [contemplatedUse, setContemplatedUse] = useState('');
  const [serverError, setServerError] = useState(null);
  const [commitMutation, isRequestInFlight] = useMutation(submitExtensionAddendumDocusignMutation);

  function submitViaDocusign(event) {
    event.preventDefault();
    event.stopPropagation();
    setServerError(null);

    function updater(store) {
      const result = store.getRootField('submitExtensionAddendumDocusign');
      const err = result.getLinkedRecord('error');

      if (err) {
        setServerError(err.getValue('message'));
      }
    }

    const variables = {
      eaInput: {
        accessRequestDraftId: accessRequestDraft.id,
        contemplatedUse,
      },
    };

    commitMutation({
      variables,
      updater,
      onCompleted: ({submitExtensionAddendumDocusign}) => {
        const {error} = submitExtensionAddendumDocusign;
        if (!error) navigate('/dashboard');
      },
      onError: () => {
        setServerError('An unexpected error occured');
      },
    });
  }

  // A primary applicant who is not a PI takes one additional applicant slot
  const remainingSigningSlots =
    (accessRequestDraft.isPi ? ADDITIONAL_APPLICANT_SLOTS : ADDITIONAL_APPLICANT_SLOTS - 1) -
    accessRequestDraft.additionalApplicants.length;
  const usApplicant = user.country === 'US';

  return (
    <div className="addendum-subsection">
      <div className="flex flex-column">
        <ExtensionAddendumSubmitMethod
          disabled={!usApplicant || remainingSigningSlots < 0}
          value={ADDENDUM_SUBMIT_METHOD.DOCUSIGN}
          icon="fa-download"
          label="Online DocuSign Form"
          selected={submitMethod === ADDENDUM_SUBMIT_METHOD.DOCUSIGN}
          setSubmitMethod={() => setSubmitMethod(ADDENDUM_SUBMIT_METHOD.DOCUSIGN)}
        />
        <ExtensionAddendumSubmitMethod
          disabled={remainingSigningSlots < 0}
          value={ADDENDUM_SUBMIT_METHOD.MANUAL}
          icon="fa-pen"
          label="Download PDF and sign"
          selected={submitMethod === ADDENDUM_SUBMIT_METHOD.MANUAL}
          setSubmitMethod={() => setSubmitMethod(ADDENDUM_SUBMIT_METHOD.MANUAL)}
        />
        {submitMethod === ADDENDUM_SUBMIT_METHOD.DOCUSIGN && (
          <>
            <div className="my-4">
              <h3>Contemplated Use</h3>
              <div className="my-2">
                <p>
                  If your contemplated use has changed since the original DAA, use this space to
                  provide your updated project description.
                </p>
                <p>You may also use this space to explain why you need to extend your agreement.</p>
              </div>
              <div className="d-flex flex-column align-items-center">
                <InputGroup>
                  <Form.Control
                    cols="120"
                    rows="10"
                    as="textarea"
                    value={contemplatedUse || ''}
                    name="request[contemplatedUse]"
                    data-testid="txt-contemplated-use"
                    onChange={e => setContemplatedUse(e.target.value)}
                    placeholder="Provide your notes here"
                  />
                </InputGroup>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 my-2">
              <Button
                variant="outline-primary"
                size="md"
                disabled={isRequestInFlight}
                onClick={() => navigate('/dashboard')}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                className="mx-1"
                disabled={isRequestInFlight}
                onClick={submitViaDocusign}
              >
                <SpinnerWrapper show={isRequestInFlight} style={{color: 'white'}} inline>
                  <i className="fa fa-paper-plane fa-lg me-2" />
                </SpinnerWrapper>{' '}
                Send Request
              </Button>
            </div>
          </>
        )}
      </div>
      {submitMethod === ADDENDUM_SUBMIT_METHOD.MANUAL && (
        <UploadExtensionAddendum className="my-4" id={iid} setUploadSuccess={setUploadSuccess} />
      )}
      {serverError && <div className="my-3 d-flex gap-2 text-danger">{serverError}</div>}
    </div>
  );
};

export default ExtensionAddendumSubmitOptions;

ExtensionAddendumSubmitOptions.propTypes = {
  queryData: PropTypes.object.isRequired,
  setUploadSuccess: PropTypes.func.isRequired,
};
